import { useState } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ReceiptText, FileText, ChevronLeft, AlertTriangle, Pencil } from "lucide-react";

const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`);
const humanize = (s) => (s || "").replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
// Backend doc_date_override expects "DD Mon YYYY" (matches every printed
// document date already) — the native <input type="date"> gives ISO, so
// convert right at the point of use rather than changing that contract.
const toDocDateFormat = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).replace(",", "");
};

/* Phase G2 (corrected) — the ONE shared guided document creator, used
   identically from Finance -> Create & Calculate and Customer 360 ->
   Finance. Two document kinds only, matching Aussie Pathways' real
   business model (G-A/G-B/G-C):
   - Payment Receipt: any actual recorded payment, at any point in the
     payment journey — full, instalment, Registration, Advance, add-on.
   - Final Tax Invoice: exactly ONE per plan, only once the final
     commercial position is determined (all final dues paid, or the plan
     canonically discontinued) — auto-resolves to the existing final_
     invoice/discontinued_invoice mechanism, never a per-instalment
     progressive invoice. Blocked outright otherwise, with a clear reason
     — never bypassable from this UI.
   Both paths are Draft/Preview -> Edit (presentation only) -> Finalize
   (G-F): nothing is numbered/immutable until the employee explicitly
   confirms on the review screen. Historical/Ad-hoc stays a completely
   separate surface (see HistoricalDocBuilder) — never used here. */
export const GuidedDocumentCreator = ({ contactId, opportunities = [], triggerLabel = "Generate document",
                                        variant = "default", size = "sm", className = "",
                                        presetOpportunityId = null, forceMode = null, onGenerated }) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("opportunity"); // opportunity | mode | pick | review
  const [oppId, setOppId] = useState(presetOpportunityId || "");
  const [ctx, setCtx] = useState(null); // finance-context for the chosen opportunity
  const [mode, setMode] = useState(null); // "receipt" | "final_invoice"
  const [receiptables, setReceiptables] = useState(null);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [preview, setPreview] = useState(null); // receipt preview OR final-invoice preview
  const [notes, setNotes] = useState("");
  const [showPresentationEdit, setShowPresentationEdit] = useState(false);
  const [lineLabel, setLineLabel] = useState(""); // presentation-only override of the printed service/line description
  const [referenceDisplay, setReferenceDisplay] = useState(""); // receipt only — presentation-only, never changes payments.reference
  const [docDate, setDocDate] = useState(""); // presentation date the document is printed with; blank = today
  const [busy, setBusy] = useState(false);
  // BUG FOUND IN PRODUCTION-ACCEPTANCE TESTING (real defect, fixed here):
  // this key used to be generated once via a bare `useState(() => ...)`
  // initializer, which only runs on the component's first mount. Since
  // this component stays mounted across many separate dialog open/close
  // cycles (one instance per plan card in FinanceC360), EVERY submission
  // from that same instance reused the identical idempotency_key — so the
  // backend's idempotency guard (correctly doing its job) returned the
  // FIRST-ever generated document on every later, genuinely different
  // receipt/invoice request, silently discarding real payments' receipts.
  // Fixed by regenerating the key each time the dialog is opened, scoping
  // it to one submission attempt (open -> pick -> review -> finalize),
  // exactly the lifetime the idempotency guard is meant to protect.
  const [idemKey, setIdemKey] = useState(null);

  const reset = () => {
    setOpen(false); setStep("opportunity"); setOppId(presetOpportunityId || "");
    setCtx(null); setMode(null); setReceiptables(null);
    setSelectedPaymentId(null); setPreview(null); setNotes("");
    setShowPresentationEdit(false); setLineLabel(""); setReferenceDisplay(""); setDocDate("");
    setIdemKey(null);
  };

  const openDialog = () => {
    setOpen(true);
    setIdemKey(`gdc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
    if (presetOpportunityId) { setOppId(presetOpportunityId); loadContext(presetOpportunityId); }
    else if (opportunities.length === 1) { setOppId(opportunities[0].opportunity_id); loadContext(opportunities[0].opportunity_id); }
  };

  const loadContext = async (id) => {
    try {
      const r = await api.get(`/finance/opportunities/${id}/finance-context`);
      setCtx(r.data);
      // Jump straight past the mode picker when a caller already knows
      // exactly which document this is (e.g. the "ready for final tax
      // invoice" banner) — same canonical preview/review screen either way.
      if (forceMode === "final_invoice" && r.data.plan_id) await chooseFinalInvoice(r.data.plan_id);
      else setStep("mode");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const chooseReceipt = async () => {
    setMode("receipt");
    const r = await api.get(`/finance/opportunities/${oppId}/receiptable-payments`);
    setReceiptables(r.data); setStep("pick");
  };

  const chooseFinalInvoice = async (planId) => {
    setMode("final_invoice");
    const r = await api.get(`/finance/plans/${planId || ctx.plan_id}/final-invoice-preview`);
    setPreview(r.data); setStep("review");
    if (r.data.eligible) setLineLabel(r.data.lines?.[0]?.label || "");
  };

  const pickPayment = async (paymentId) => {
    setSelectedPaymentId(paymentId);
    const r = await api.get(`/finance/payments/${paymentId}/receipt-preview`);
    setPreview(r.data); setStep("review");
    setLineLabel(r.data.obligation?.label ? `${r.data.obligation.label} (payment received)` : "");
    setReferenceDisplay(r.data.reference || "");
  };

  const finalize = async () => {
    setBusy(true);
    try {
      let r;
      if (mode === "receipt") {
        r = await api.post(`/finance/payments/${selectedPaymentId}/receipt`, {
          notes: notes || null, idempotency_key: idemKey,
          line_label_override: showPresentationEdit && lineLabel ? lineLabel : null,
          reference_display_override: showPresentationEdit && referenceDisplay ? referenceDisplay : null,
          doc_date_override: showPresentationEdit && docDate ? toDocDateFormat(docDate) : null,
        });
      } else {
        r = await api.post(`/finance/plans/${ctx.plan_id}/final-invoice`, {
          notes: notes || null, idempotency_key: idemKey,
          line_label_override: showPresentationEdit && lineLabel ? lineLabel : null,
          doc_date_override: showPresentationEdit && docDate ? toDocDateFormat(docDate) : null,
        });
      }
      toast.success(`${r.data.doc_number} generated, stored and queued for delivery`);
      reset(); onGenerated?.();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  const opp = opportunities.find((o) => o.opportunity_id === oppId);

  return (
    <>
      {/* Wraps instead of forcing its width (2026-09 R6 mobile pass): the label
          carries the product/plan name, which on a phone was wider than the
          page and pushed Customer 360 into horizontal scroll. */}
      <Button size={size} variant={variant} className={cn("max-w-full h-auto min-h-8 whitespace-normal text-left", className)}
        data-testid="guided-doc-trigger" onClick={openDialog}>
        <ReceiptText className="h-4 w-4 mr-1 shrink-0" />{triggerLabel}
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Generate receipt or tax invoice</DialogTitle></DialogHeader>

          {step === "opportunity" && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Choose the opportunity/product this document is about.</p>
              <Select value={oppId} onValueChange={(v) => { setOppId(v); loadContext(v); }}>
                <SelectTrigger data-testid="guided-doc-opportunity-select"><SelectValue placeholder="Choose opportunity…" /></SelectTrigger>
                <SelectContent>
                  {opportunities.map((o) => <SelectItem key={o.opportunity_id} value={o.opportunity_id}>{o.product_name || o.product_code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {step === "mode" && ctx && (
            <div className="space-y-3">
              <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => setStep("opportunity")}>
                <ChevronLeft className="h-3 w-3" /> Back
              </button>
              <div className="rounded-md border bg-muted/40 p-2.5 text-xs">
                <p><b>{opp?.product_name || ctx.product_code}</b></p>
                {ctx.plan_id ? <p className="text-muted-foreground">Payment Plan: {ctx.plan_id}</p>
                  : <p className="text-amber-600 dark:text-amber-400">No formal Payment Plan yet — a Receipt can still be generated for any recorded pre-Signup payment.</p>}
              </div>
              <div className="grid grid-cols-1 gap-2">
                <Button data-testid="guided-doc-mode-receipt" variant="outline" className="justify-start h-auto py-2.5" onClick={chooseReceipt}>
                  <ReceiptText className="h-4 w-4 mr-2 shrink-0" />
                  <span className="text-left"><span className="block text-sm font-medium">Payment Receipt</span>
                    <span className="block text-xs text-muted-foreground">Choose a recorded payment — SalesOS derives everything else.</span></span>
                </Button>
                {ctx.plan_id && (
                  <Button data-testid="guided-doc-mode-final-invoice" variant="outline" className="justify-start h-auto py-2.5" onClick={chooseFinalInvoice}>
                    <FileText className="h-4 w-4 mr-2 shrink-0" />
                    <span className="text-left"><span className="block text-sm font-medium">Final Tax Invoice</span>
                      <span className="block text-xs text-muted-foreground">Only once the final commercial position is determined — one per plan.</span></span>
                  </Button>
                )}
              </div>
            </div>
          )}

          {step === "pick" && mode === "receipt" && (
            <div className="space-y-2">
              <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => setStep("mode")}>
                <ChevronLeft className="h-3 w-3" /> Back
              </button>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Choose the payment</p>
              {(!receiptables || receiptables.length === 0) && <p className="text-sm text-muted-foreground">No recorded payments found on this opportunity yet.</p>}
              {(receiptables || []).map((p) => (
                <button key={p.payment_id} data-testid={`guided-doc-payment-${p.payment_id}`} disabled={p.already_receipted}
                  className="w-full text-left p-2.5 rounded-md border text-sm hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 flex-wrap"
                  onClick={() => pickPayment(p.payment_id)}>
                  <span className="font-medium">{p.obligation_label || p.obligation_key}</span>
                  <Badge variant="outline" className="text-[10px]">{p.classification || "instalment"}</Badge>
                  <span className="ml-auto font-mono">{inr(p.amount)}</span>
                  <span className="text-xs text-muted-foreground w-full">{p.paid_at} · {p.method}
                    {p.verification_status !== "verified" && <span className="text-amber-600 dark:text-amber-400"> · not yet verified</span>}
                    {p.already_receipted && <span> · already receipted ({p.existing_doc_number})</span>}
                  </span>
                </button>
              ))}
            </div>
          )}

          {step === "review" && preview && mode === "receipt" && (
            <div className="space-y-3">
              <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => setStep("pick")}>
                <ChevronLeft className="h-3 w-3" /> Back
              </button>
              <div className="rounded-md border p-3 text-sm space-y-1.5" data-testid="guided-doc-review-receipt">
                <Row label="Customer" value={`${preview.customer?.name} (${preview.customer?.customer_id})`} />
                <Row label="Product" value={preview.product_name} />
                <Row label="Related instalment / add-on" value={preview.obligation?.label} />
                {preview.obligation?.is_pre_signup && <Row label="" value="Pre-Signup payment — no formal Payment Plan existed yet" muted />}
                <Row label="Payment date" value={preview.payment_date} />
                <Row label="Method" value={preview.method} />
                <Row label="Reference" value={preview.reference || "—"} />
                <Row label="Verification" value={humanize(preview.verification_status)} />
                <Separator />
                <Row label="Amount" value={<b>{inr(preview.amount)}</b>} />
                {preview.gst_presentation && (
                  <p className="text-xs text-muted-foreground">GST-inclusive at {preview.gst_presentation.rate}% — taxable value {inr(preview.gst_presentation.taxable_value)}, GST {inr(preview.gst_presentation.gst)}.</p>
                )}
              </div>
              <PresentationEditPanel show={showPresentationEdit} setShow={setShowPresentationEdit}
                lineLabel={lineLabel} setLineLabel={setLineLabel}
                referenceDisplay={referenceDisplay} setReferenceDisplay={setReferenceDisplay}
                docDate={docDate} setDocDate={setDocDate} showReference />
              <Textarea data-testid="guided-doc-notes" placeholder="Presentation note (optional) — this never changes the amount." value={notes} onChange={(e) => setNotes(e.target.value)} />
              <p className="text-[11px] text-muted-foreground">The amount is derived from the recorded payment and cannot be edited here — correct the underlying payment record first if it's wrong.</p>
            </div>
          )}

          {step === "review" && preview && mode === "final_invoice" && !preview.eligible && (
            <div className="space-y-3">
              <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => setStep("mode")}>
                <ChevronLeft className="h-3 w-3" /> Back
              </button>
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm space-y-1.5" data-testid="guided-doc-final-invoice-blocked">
                <div className="flex items-center gap-2 font-medium text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="h-4 w-4" /> Final Tax Invoice not available yet
                </div>
                <p className="text-xs text-muted-foreground">{preview.reason}</p>
                {preview.reason_code === "final_financial_position_not_determined" && (
                  <p className="text-xs text-muted-foreground">The customer may still complete the package and pay in full, or discontinue before the remaining instalment falls due — SalesOS waits until one of those is actually true before a Tax Invoice can be issued. A Payment Receipt can still be generated for any payment already received.</p>
                )}
              </div>
            </div>
          )}

          {step === "review" && preview && mode === "final_invoice" && preview.eligible && (
            <div className="space-y-3">
              <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => setStep("mode")}>
                <ChevronLeft className="h-3 w-3" /> Back
              </button>
              <div className="rounded-md border p-3 text-sm space-y-1.5" data-testid="guided-doc-review-final-invoice">
                <Row label="Customer" value={`${preview.customer?.name} (${preview.customer?.customer_id})`} />
                <Row label="Product" value={preview.product_name} />
                <Row label="Commercial Plan version" value={preview.commercial_plan_version ? `v${preview.commercial_plan_version}` : "—"} />
                <Row label="Document" value={preview.resolved_doc_type === "discontinued_invoice" ? "Discontinued Tax Invoice" : "Tax Invoice"} />
                {preview.financial_position && (
                  <>
                    <Separator />
                    <Row label="Original commercial value" value={inr(preview.financial_position.original_obligation_total)} />
                    <Row label="Cash received" value={inr(preview.financial_position.cash_received)} />
                    {Number(preview.financial_position.reallocation_credit_in) > 0 && (
                      <Row label="Reallocated in (non-cash)" value={inr(preview.financial_position.reallocation_credit_in)} />
                    )}
                    {Number(preview.financial_position.leadership_waived) > 0 && (
                      <Row label="Leadership-approved waiver" value={<span className="text-sky-600 dark:text-sky-400">{inr(preview.financial_position.leadership_waived)} (not cash)</span>} />
                    )}
                    {Number(preview.financial_position.cash_refunded) > 0 && (
                      <Row label="Actual cash refunded" value={<span className="text-amber-600 dark:text-amber-400">{inr(preview.financial_position.cash_refunded)}</span>} />
                    )}
                  </>
                )}
                <Separator />
                <Row label="Taxable value" value={inr(preview.summary?.taxable_value)} />
                <Row label="GST" value={inr(preview.summary?.gst)} />
                {preview.summary?.reimbursement && <Row label="Reimbursement (no GST)" value={inr(preview.summary.reimbursement)} />}
                <Row label="Invoice gross total" value={<b>{inr(preview.summary?.total)}</b>} />
                {preview.discontinued && <Row label="Discontinuation status" value="Discontinued / NPA" />}
              </div>
              <PresentationEditPanel show={showPresentationEdit} setShow={setShowPresentationEdit}
                lineLabel={lineLabel} setLineLabel={setLineLabel}
                docDate={docDate} setDocDate={setDocDate} showReference={false} />
              <Textarea data-testid="guided-doc-notes" placeholder="Presentation note (optional) — this never changes any monetary figure." value={notes} onChange={(e) => setNotes(e.target.value)} />
              <p className="text-[11px] text-muted-foreground">Taxable value, GST and totals come from the existing revised final-invoice calculation and cannot be edited here — a genuine correction goes through the underlying Finance record first.</p>
            </div>
          )}

          {step === "review" && preview && (mode === "receipt" || preview.eligible) && (
            <DialogFooter>
              <Button data-testid="guided-doc-finalize" disabled={busy} onClick={finalize}>
                {busy ? "Generating…" : "Confirm & generate"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const Row = ({ label, value, muted }) => (
  <div className="flex items-center justify-between gap-3">
    {label && <span className="text-xs text-muted-foreground shrink-0">{label}</span>}
    <span className={`text-right ${muted ? "text-xs text-muted-foreground" : "text-sm"}`}>{value}</span>
  </div>
);

/* Phase G-F (correction round) — the ONLY editable fields on either review
   screen: how the document is PRESENTED, never what it says financially.
   Collapsed by default so the common case (issue it as calculated) stays
   one click; expanding never pre-applies anything until Confirm & generate
   is actually pressed (the toggle itself gates whether these values are
   sent at all — see finalize()). */
const PresentationEditPanel = ({ show, setShow, lineLabel, setLineLabel,
                                referenceDisplay, setReferenceDisplay,
                                docDate, setDocDate, showReference }) => (
  <div className="rounded-md border p-2.5 space-y-2" data-testid="guided-doc-presentation-panel">
    <button type="button" className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
      onClick={() => setShow(!show)} data-testid="guided-doc-presentation-toggle">
      <Pencil className="h-3.5 w-3.5" /> {show ? "Hide" : "Edit"} presentation details
    </button>
    {show && (
      <div className="space-y-2 pt-1">
        <div className="space-y-1">
          <Label className="text-xs">Line / service description shown on the document</Label>
          <Input data-testid="guided-doc-line-label" value={lineLabel} onChange={(e) => setLineLabel(e.target.value)} />
        </div>
        {showReference && (
          <div className="space-y-1">
            <Label className="text-xs">Payment reference as shown on the document</Label>
            <Input data-testid="guided-doc-reference-display" value={referenceDisplay} onChange={(e) => setReferenceDisplay(e.target.value)} />
            <p className="text-[10px] text-muted-foreground">The recorded transaction reference itself is never changed — this only affects what prints.</p>
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-xs">Document date (defaults to today)</Label>
          <Input data-testid="guided-doc-date-override" type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} max={new Date().toISOString().slice(0, 10)} />
        </div>
        <p className="text-[10px] text-muted-foreground">These change only how the document reads — amounts, GST and the underlying Finance ledger are never affected.</p>
      </div>
    )}
  </div>
);
